import {
  builtInOrderedOrigins,
  FieldOrigin,
  groupedFields,
  ISelectRangeOption,
  ISelectValueOption,
  LoanFieldProps,
  ltvFieldKeys,
} from 'config'
import { canAddNewField, getLoanInputFields } from 'config/loan.input.fields.constants'
import { defaultInputs as borrowerInformationInputs } from 'pages/LoanApplication/BorrowerInformation/constants'
import { financesInputs, hmdaInputs, propertyInputs } from 'pages/LoanApplication/DeclarationsHmda/constants'
import {
  defaultInputs as propertyInformationInputs,
  propertyExtraSectionAdminTitles,
  propertyExtraSectionKeys,
  propertyExtraSectionTitles,
} from 'pages/LoanApplication/PropertyInformation/constants'
import { signDefaultFieldKeys } from 'pages/LoanApplication/SignApplication/constants'
import { defaultOriginatorInformationConfig } from 'pages/LoanApplication/SignApplication/originatorInformation.constants'

export const creditDefaultFieldKeys = ['template']
import { IBankruptcyRange, IMortgageLatesRange } from 'pages/Resources'
import { convertNullToBlank, getBankruptcyLabel, getMortgageLatesLabel } from 'pages/Resources/GuidelineOverlay/logic'
import { isEmpty, isNull } from 'utils'

import { ISelectOption } from '../../BorrowerPipelineConfiguration'
import { assetLiabilityInputs } from './inputs'
import { InterestOptions, InterestOptionsMap } from './VisiblePropertyModal/constants'

export const setLoanFieldDefaultSection = (
  data: LoanFieldProps,
  options: { combineLinkedFields: boolean } = { combineLinkedFields: false },
) => {
  const { combineLinkedFields } = options

  builtInOrderedOrigins.forEach((origin) => {
    if (!data[origin]) data[origin] = {}
  })

  const borrowerInputs = borrowerInformationInputs(data[FieldOrigin.BorrowerInformation])
  const propInfoInputs = propertyInformationInputs(data[FieldOrigin.PropertyInformation])

  const defaultSectionFields: any = {
    [FieldOrigin.BorrowerInformation]: borrowerInputs,
    [FieldOrigin.PropertyInformation]: propInfoInputs,
    [FieldOrigin.AssetLiability]: assetLiabilityInputs(),
  }

  Object.keys(defaultSectionFields).forEach((fieldOrigin) => {
    const fields = Object.keys(defaultSectionFields[fieldOrigin])
    fields.forEach((fieldKey) => {
      if (
        fieldOrigin === FieldOrigin.PropertyInformation &&
        (propertyExtraSectionKeys as readonly string[]).includes(fieldKey)
      )
        return
      if (!data[fieldOrigin][fieldKey]) data[fieldOrigin][fieldKey] = {}
      data[fieldOrigin][fieldKey].section = `1`
    })
  })

  Object.keys(propertyInputs).forEach((fieldKey) => {
    if (!data[FieldOrigin.DeclarationsHMDA][fieldKey]) data[FieldOrigin.DeclarationsHMDA][fieldKey] = {}
    data[FieldOrigin.DeclarationsHMDA][fieldKey].section = `1`
  })

  Object.keys(financesInputs).forEach((fieldKey) => {
    if (!data[FieldOrigin.DeclarationsHMDA][fieldKey]) data[FieldOrigin.DeclarationsHMDA][fieldKey] = {}
    data[FieldOrigin.DeclarationsHMDA][fieldKey].section = `2`
  })

  Object.keys(hmdaInputs()).forEach((fieldKey) => {
    if (!data[FieldOrigin.DeclarationsHMDA][fieldKey]) data[FieldOrigin.DeclarationsHMDA][fieldKey] = {}
    data[FieldOrigin.DeclarationsHMDA][fieldKey].section = `4`
  })

  const originalFieldKeys = [
    ...Object.keys(propertyInputs),
    ...Object.keys(financesInputs),
    ...Object.keys(hmdaInputs()),
  ]

  Object.keys(data[FieldOrigin.DeclarationsHMDA])
    .filter((fieldKey) => !originalFieldKeys.includes(fieldKey))
    .forEach((fieldKey) => {
      if (!data[FieldOrigin.DeclarationsHMDA][fieldKey]) data[FieldOrigin.DeclarationsHMDA][fieldKey] = {}
      if (!data[FieldOrigin.DeclarationsHMDA][fieldKey].section)
        data[FieldOrigin.DeclarationsHMDA][fieldKey].section = `3`
      if (!data[FieldOrigin.DeclarationsHMDA][fieldKey].inputType)
        data[FieldOrigin.DeclarationsHMDA][fieldKey].inputType = `ToggleButton`
    })

  const signOrigin = 'sign' as FieldOrigin
  if (!data[signOrigin]) data[signOrigin] = {}
  signDefaultFieldKeys.forEach((fieldKey) => {
    if (!data[signOrigin][fieldKey]) data[signOrigin][fieldKey] = {}
    if (!data[signOrigin][fieldKey].section) data[signOrigin][fieldKey].section = `1`
  })
  if (data[signOrigin].originatorInformation) {
    const originator = data[signOrigin].originatorInformation
    if (originator.visible === undefined) originator.visible = true
    if (!originator.title) originator.title = 'Loan Originator Information'
    if (!originator.defaultValue) {
      originator.defaultValue = defaultOriginatorInformationConfig()
    }
  }

  const creditOrigin = 'credit' as FieldOrigin
  if (!data[creditOrigin]) data[creditOrigin] = {}
  creditDefaultFieldKeys.forEach((fieldKey) => {
    if (!data[creditOrigin][fieldKey]) data[creditOrigin][fieldKey] = {}
    if (!data[creditOrigin][fieldKey].section) data[creditOrigin][fieldKey].section = `1`
  })

  const defaultSections = {
    [FieldOrigin.LoanStructure]: groupedFields.map((fields, index) => {
      const newId = index + 1

      fields.forEach((fieldKey) => {
        if (!data[FieldOrigin.LoanStructure][fieldKey]) data[FieldOrigin.LoanStructure][fieldKey] = {}
        data[FieldOrigin.LoanStructure][fieldKey].section = `${newId}`

        if (data.sections && data.sections[FieldOrigin.LoanStructure]) {
          const newIndex = data.sections?.[FieldOrigin.LoanStructure]?.findIndex((v) => v.fields.includes(fieldKey))
          if (newIndex == undefined || newIndex == -1) data[FieldOrigin.LoanStructure][fieldKey].section = ''
          else
            data[FieldOrigin.LoanStructure][fieldKey].section = `${
              data.sections[FieldOrigin.LoanStructure]?.[newIndex].id
            }`
        }
      })

      return {
        id: newId,
        fields,
        title: `Section ${index + 1}`,
      }
    }),
    [FieldOrigin.BorrowerInformation]: [
      {
        id: 1,
        fields: Object.keys(borrowerInputs),
        title: 'Section 1',
      },
    ],
    [FieldOrigin.PropertyInformation]: [
      {
        id: 1,
        fields: Object.keys(propInfoInputs).filter(
          (key) => !(propertyExtraSectionKeys as readonly string[]).includes(key),
        ),
        title: 'Section 1',
      },
    ],
    [FieldOrigin.EmploymentAndIncome]: [
      {
        id: 1,
        fields: [],
        title: 'Employment and Income',
      },
    ],
    [FieldOrigin.AssetLiability]: [
      {
        id: 1,
        fields: Object.keys(assetLiabilityInputs()),
        title: 'Section 1',
      },
    ],
    [FieldOrigin.DeclarationsHMDA]: [
      {
        id: 1,
        fields: Object.keys(propertyInputs),
        title: 'About this Property and Your Money for this Loan',
      },
      {
        id: 2,
        fields: Object.keys(financesInputs),
        title: 'About Your Finances',
      },
      {
        id: 3,
        fields: Object.keys(data[FieldOrigin.DeclarationsHMDA]).filter(
          (fieldKey) => !originalFieldKeys.includes(fieldKey),
        ),
        title: 'Additional',
      },
      {
        id: 4,
        fields: Object.keys(hmdaInputs()),
        title: 'HMDA',
      },
    ],
    [signOrigin]: [
      {
        id: 1,
        fields: [...signDefaultFieldKeys],
        title: 'Section 1',
      },
    ],
    [creditOrigin]: [
      {
        id: 1,
        fields: [...creditDefaultFieldKeys],
        title: 'Section 1',
      },
    ],
  }

  if (!data.sections) data.sections = {}

  Object.keys(defaultSections).forEach((_fieldOrigin: any) => {
    const fieldOrigin = _fieldOrigin as FieldOrigin
    if (data.sections && data.sections[fieldOrigin]) return
    ;(data.sections as any)[fieldOrigin] = (defaultSections as any)[fieldOrigin]
  })

  const signSections = data.sections![signOrigin]
  if (signSections?.length && data[signOrigin]?.originatorInformation) {
    const section =
      signSections.find((v) => v.fields.includes('template')) ||
      signSections.find((v) => v.id === 1) ||
      signSections[0]
    if (section) {
      data[signOrigin].originatorInformation.section = `${section.id}`
      if (!section.fields.includes('originatorInformation')) {
        const templateIndex = section.fields.indexOf('template')
        if (templateIndex >= 0) section.fields.splice(templateIndex + 1, 0, 'originatorInformation')
        else section.fields.push('originatorInformation')
      }
    }
  }

  const loanStructureSections = data.sections![FieldOrigin.LoanStructure]
  if (loanStructureSections) {
    const proposedLoanAmountSection = loanStructureSections.find((v) => v.fields.includes('proposedLoanAmount'))
    if (proposedLoanAmountSection) {
      ltvFieldKeys.forEach((fieldKey) => {
        if (!data[FieldOrigin.LoanStructure][fieldKey]) data[FieldOrigin.LoanStructure][fieldKey] = {}
        data[FieldOrigin.LoanStructure][fieldKey].section = `${proposedLoanAmountSection.id}`
        if (!proposedLoanAmountSection.fields.includes(fieldKey)) proposedLoanAmountSection.fields.push(fieldKey)
      })
    }

    const additionalPropertyAddressSection = loanStructureSections.find((v) =>
      v.fields.includes('additionalPropertyAddress'),
    )
    if (additionalPropertyAddressSection) {
      const fieldKey = 'subjectPropertyMap'
      if (!data[FieldOrigin.LoanStructure][fieldKey]) data[FieldOrigin.LoanStructure][fieldKey] = {}
      data[FieldOrigin.LoanStructure][fieldKey].section = `${additionalPropertyAddressSection.id}`
      if (!additionalPropertyAddressSection.fields.includes(fieldKey)) {
        const index = additionalPropertyAddressSection.fields.indexOf('additionalPropertyAddress')
        additionalPropertyAddressSection.fields.splice(index + 1, 0, fieldKey)
      }
    }

    const alreadyPatchedSummary = Object.keys(data[FieldOrigin.LoanStructure] || {}).some(
      (fieldKey) => fieldKey === 'summaryOfAllProperties',
    )
    if (!alreadyPatchedSummary) {
      const sectionIndex = loanStructureSections.findIndex((section) =>
        section.fields?.includes('subjectPropertyAddress'),
      )
      if (sectionIndex !== -1) {
        const newSectionId = Date.now()
        const newFieldKey = 'summaryOfAllProperties'
        loanStructureSections.splice(sectionIndex, 0, {
          id: newSectionId,
          title: 'Summary of All Properties',
          fields: [newFieldKey],
        })
        data[FieldOrigin.LoanStructure][newFieldKey] = {
          inputType: 'custom',
          title: 'Summary of All Properties',
          section: `${newSectionId}`,
        }
      }
    }
  }

  const borrowerInformationSections = data.sections![FieldOrigin.BorrowerInformation]

  if (!data[FieldOrigin.BorrowerInformation]['layerCount']?.visibleLogic) {
    data[FieldOrigin.BorrowerInformation]['layerCount'] = {
      ...(data[FieldOrigin.BorrowerInformation]['layerCount'] || {}),
      visibleLogic: [[{ fieldOrigin: FieldOrigin.BorrowerInformation, fieldKey: 'isLayeredEntity', value: true }]],
    }
  }

  if (borrowerInformationSections) {
    const ownershipPercentageSection = borrowerInformationSections.find((v) => v.fields.includes('ownershipPercentage'))
    if (ownershipPercentageSection) {
      ;['layerCount', 'isLayeredEntity'].forEach((fieldKey) => {
        if (!data[FieldOrigin.BorrowerInformation][fieldKey]) data[FieldOrigin.BorrowerInformation][fieldKey] = {}
        if (!data[FieldOrigin.BorrowerInformation][fieldKey].section) return

        data[FieldOrigin.BorrowerInformation][fieldKey].section = `${ownershipPercentageSection.id}`
        if (!ownershipPercentageSection.fields.includes(fieldKey)) {
          const index = ownershipPercentageSection.fields.indexOf('ownershipPercentage')
          ownershipPercentageSection.fields.splice(index + 1, 0, fieldKey)
        }
      })
    }
  }

  // Seed Property Information extra sections (other mortgages / rental income / gifts) as
  // their own sections, hidden by default. Do not overwrite visible if already configured.
  {
    const propOrigin = FieldOrigin.PropertyInformation
    if (!data[propOrigin]) data[propOrigin] = {}
    if (!data.sections) data.sections = {}
    if (!data.sections[propOrigin]) data.sections[propOrigin] = []

    const propSections = data.sections[propOrigin]!

    // One admin section with three toggles — short labels, no duplicated long form titles.
    const sharedSectionTitle = 'Optional Property Sections'
    let sharedSection = propSections.find(
      (s) =>
        s.title === sharedSectionTitle ||
        (s.fields.length > 0 &&
          s.fields.every((key) => (propertyExtraSectionKeys as readonly string[]).includes(key)) &&
          s.fields.length === propertyExtraSectionKeys.length),
    )

    propertyExtraSectionKeys.forEach((fieldKey) => {
      const adminTitle = propertyExtraSectionAdminTitles[fieldKey]
      const alreadyExists = !!data[propOrigin][fieldKey]
      if (!alreadyExists) {
        data[propOrigin][fieldKey] = {
          inputType: 'ToggleButton',
          title: adminTitle,
          visible: false,
        }
      } else {
        if (
          !data[propOrigin][fieldKey].title ||
          data[propOrigin][fieldKey].title === propertyExtraSectionTitles[fieldKey]
        ) {
          data[propOrigin][fieldKey].title = adminTitle
        }
        if (!data[propOrigin][fieldKey].inputType || data[propOrigin][fieldKey].inputType === 'Section') {
          data[propOrigin][fieldKey].inputType = 'ToggleButton'
        }
      }

      // Pull out of Section 1 / old dedicated long-title sections.
      propSections.forEach((section) => {
        if (section === sharedSection) return
        if (section.fields.includes(fieldKey)) {
          section.fields = section.fields.filter((key) => key !== fieldKey)
        }
      })
    })

    // Drop empty leftover sections created by the previous long-title seeding.
    for (let i = propSections.length - 1; i >= 0; i--) {
      const section = propSections[i]
      if (section.fields.length === 0 && section.id !== 1) propSections.splice(i, 1)
    }

    if (!sharedSection) {
      sharedSection = {
        id: Date.now(),
        title: sharedSectionTitle,
        fields: [...propertyExtraSectionKeys],
      }
      propSections.push(sharedSection)
    } else {
      sharedSection.title = sharedSectionTitle
      propertyExtraSectionKeys.forEach((fieldKey) => {
        if (!sharedSection!.fields.includes(fieldKey)) sharedSection!.fields.push(fieldKey)
      })
    }

    propertyExtraSectionKeys.forEach((fieldKey) => {
      data[propOrigin][fieldKey].section = `${sharedSection!.id}`
    })
  }

  if (!data.order) data.order = [...builtInOrderedOrigins] as any

  // Ensure every built-in origin is present in the order, inserted at the
  // correct position relative to its neighbours in builtInOrderedOrigins.
  builtInOrderedOrigins.forEach((origin, canonicalIndex) => {
    if (data.order?.includes(origin as any)) return
    let insertAfterIndex = -1
    for (let i = canonicalIndex - 1; i >= 0; i--) {
      const idx = data.order!.indexOf(builtInOrderedOrigins[i] as any)
      if (idx !== -1) {
        insertAfterIndex = idx
        break
      }
    }
    if (insertAfterIndex >= 0) data.order!.splice(insertAfterIndex + 1, 0, origin as any)
    else data.order!.push(origin as any)
  })

  data.order!.push(
    ...(Object.keys(data).filter(
      (key) =>
        ![
          FieldOrigin.LoanStructure,
          'sections',
          'order',
          'customTitle',
          'visibility',
          'additionalInputs',
          'properties',
        ].includes(key) && !data.order?.includes(key as any),
    ) as FieldOrigin[]),
  )

  if (combineLinkedFields) {
    const inputs = getLoanInputFields()
    Object.keys(data).forEach((fieldOrigin: any) => {
      if (!canAddNewField(fieldOrigin)) return
      Object.keys(data[fieldOrigin]).forEach((fieldKey) => {
        const input = data[fieldOrigin][fieldKey]
        const linkedField = input?.linkedField
        if (!linkedField) return
        const { fieldOrigin: linkedFieldOrigin, fieldKey: linkedFieldKey } = linkedField
        if (!linkedFieldOrigin || !linkedFieldKey) return
        data[fieldOrigin][fieldKey] = {
          ...(inputs[linkedFieldOrigin][linkedFieldKey] as any),
          ...input,
          // title: `${input.title} (Linked)`,
        }
      })
    })
  }

  return data
}

export const convertSelectRangeOptionsToMap = (
  options?: ISelectRangeOption[] | ISelectValueOption[] | ISelectOption[],
): Record<string, string> => {
  if (!options || !options?.length) return {}

  if (options[0] && 'days' in options[0])
    return (options as ISelectRangeOption[])
      .filter((v) => v.visible !== false)
      .reduce((acc: Record<string, string>, opt) => {
        const { from, title, to = null, days = null, months = null } = opt
        acc[`${from}-${to}-${days}-${months}`] = title
        return acc
      }, {})
  else if (options[0] && 'chapter' in options[0])
    return (options as ISelectRangeOption[])
      .filter((v) => v.visible !== false)
      .reduce((acc: Record<string, string>, opt) => {
        const { from, title, to = null, chapter = null } = opt
        acc[`${chapter}-${from}-${to}`] = title
        return acc
      }, {})
  else if (options[0] && 'from' in options[0])
    return (options as ISelectRangeOption[])
      .filter((v) => v.visible !== false)
      .reduce((acc: Record<string, string>, opt) => {
        const { from, title, to = null } = opt
        acc[`${from}-${to}`] = title
        return acc
      }, {})
  else if (options[0] && 'subValue' in options[0])
    return (options as ISelectValueOption[])
      .filter((v) => v.visible !== false)
      .reduce((acc: Record<string, string>, opt) => {
        const { value, subValue, interestOption, monthsInterest, title } = opt
        let key = `${value}-${subValue}`
        if (!isEmpty(interestOption)) {
          if (interestOption == InterestOptions.InterestBased) key += `-${interestOption}-${monthsInterest}`
          else if (interestOption == InterestOptions.NonInterestBased) key += `-${interestOption}`
        }
        acc[key] = title
        return acc
      }, {})
  else
    return (options as Array<ISelectValueOption | ISelectOption>)
      .filter((v) => v.visible !== false)
      .reduce((acc: Record<string, string>, opt) => {
        const mapKey = 'key' in opt && opt.key ? opt.key : 'value' in opt ? opt.value : undefined
        if (mapKey === undefined || mapKey === null || mapKey === '') return acc
        acc[String(mapKey)] = opt.title
        return acc
      }, {})
}

export const rangeToTitle = (value: ISelectRangeOption, key: string) => {
  const { from, to } = value
  let suffix = ''
  switch (key) {
    case 'experience':
      suffix = (from === 0 && to === 0) || (from === 1 && to === 1) ? ' Transaction' : ' Transactions'
      break
    case 'foreclosure':
    case 'deedInLieu':
    case 'shortSale':
    case 'forbearanceLoanModification':
      suffix = ' Months'
      break
  }

  if (isNull(from) && isNull(to)) return `None`
  else if (!isNull(from) && isNull(to)) return `${from}+${suffix}`
  else if (isNull(from) && !isNull(to)) return `<= ${to}${suffix}`
  else {
    if (from === to) return `${from}${suffix}`
    return `${from} - ${to}${suffix}`
  }
}

export const valueToTitle = (data: ISelectValueOption, key: string) => {
  const { value, subValue, interestOption, monthsInterest } = data
  let suffix = ''
  switch (key) {
    case 'loanTerm':
      suffix = [0, 1].includes(value) ? ' Month' : ' Months'
      break
    case 'prepaymentPenalty':
      suffix = [0, 1].includes(value) ? ` Month - ${subValue || ''}` : ` Months - ${subValue || ''}`
      if (!isEmpty(interestOption)) {
        if (interestOption == InterestOptions.InterestBased)
          suffix += ` - ${InterestOptionsMap[interestOption]} - ${monthsInterest} Months Interest`
        else if (interestOption == InterestOptions.NonInterestBased)
          suffix += ` - ${InterestOptionsMap[interestOption]}`
      }
      break
  }

  if (isNull(value)) return `None`
  else return `${value}${suffix}`
}

export const generateTitleFromRange = (
  range: ISelectRangeOption | ISelectValueOption | ISelectOption,
  key: string,
): string => {
  if (!range) return ''
  if ('key' in range) return range.title
  if ('value' in range) {
    return valueToTitle(range, key)
  } else if ('from' in range) {
    if (key === 'bankruptcy') return getBankruptcyLabel(range as IBankruptcyRange)
    else if (key === 'mortgageLates') return getMortgageLatesLabel(range as IMortgageLatesRange)
    else return rangeToTitle(range, key)
  }
  return ''
}

export const generateTitle = (fieldKey: string, option: any, index: number): string => {
  const { chapter, from, to, days, months, value, subValue, interestOption, monthsInterest } = option as any

  if ('value' in option) {
    if (fieldKey === 'prepaymentPenalty') {
      let rlt = `${index + 1} ${value}-${subValue || ''}`
      if (!isEmpty(interestOption)) {
        if (interestOption == InterestOptions.InterestBased) rlt += `-${interestOption}-${monthsInterest}`
        else if (interestOption == InterestOptions.NonInterestBased) rlt += `-${interestOption}`
      }
      return rlt
    } else return `${index + 1} Title ${index + 1}`
  } else if ('from' in option) {
    if (fieldKey === 'bankruptcy')
      return `${index + 1} ${convertNullToBlank(chapter)}-${convertNullToBlank(from)}-${convertNullToBlank(to)}`
    else if (fieldKey === 'mortgageLates')
      return `${index + 1} ${convertNullToBlank(from)}-${convertNullToBlank(to)}-${convertNullToBlank(
        days,
      )}-${convertNullToBlank(months)}`
    else return `${index + 1} ${convertNullToBlank(from)}-${convertNullToBlank(to)}`
  } else return `${index + 1} Title ${index + 1}`
}
